import { useTranslation } from 'react-i18next';
import intersection from 'lodash/intersection';
import { ComponentProps } from 'react';

import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { useShowSitePartitioningItems } from 'utils/customHooks/useShowSitePartitioningItems';

import { OpDataFetchTransfer } from '../OpDataFetchTransfer/OpDataFetchTransfer';
import { OpText } from '../OpText/OpText';

import { OpSiteSpecificGroupDataFetchTransferProps } from './types';

/**
 * Component that wraps `OpDataFetchTransfer` to fetch and display groups in a way appropriate for
 * site-specific access when enabled.
 *
 * @param {OpSiteSpecificGroupDataFetchTransferProps} props - The properties for the component.
 * @param {number} props.orgId - The organization ID used to fetch site data.
 * @param {object} props.existingItemsQueryOptions - Query options for fetching existing items.
 * @param {object} props.existingItemIdsQueryOptions - Query options for fetching existing item IDs.
 * @param {string} [props.existingItemsLabel] - Label for the existing items section.
 * @param {function} [props.createDisabledAvailableItem] - Function that takes an item and returns a boolean indicating if it should be disabled.
 * @param {function} [props.createAvailableItemTooltip] - Function that takes an item and returns a string for the tooltip.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const OpSiteSpecificGroupDataFetchTransfer = ({
  orgId,
  existingItemsQueryOptions,
  existingItemIdsQueryOptions,
  existingItemsLabel,
  createDisabledAvailableItem,
  createAvailableItemTooltip,
  ...opSiteSpecificGroupDataFetchTransferProps
}: OpSiteSpecificGroupDataFetchTransferProps) => {
  const { t } = useTranslation();

  const { isShowingSitePartitioningItems } =
    useShowSitePartitioningItems(orgId);

  const { data: sitesData } = useOpQuery({
    apiEndpointName: 'listSites',
    parameters: [orgId],
    select: (heliumResponse) => heliumResponse?.json?.data,
  });

  const createLabel: ComponentProps<
    typeof OpDataFetchTransfer
  >['createLabel'] = (record) => (
    <div>
      <OpText ellipsis>{record.title || '--'}</OpText>
      <OpText
        ellipsis
        size="small"
        className="op-data-fetch-transfer__sub-label"
      >
        {record.description}
      </OpText>
    </div>
  );

  return (
    <OpDataFetchTransfer
      availableItemsParams={{
        queryOptions: {
          apiEndpointName: 'listGroupsPost',
          parameters: [
            orgId,
            {
              ...(isShowingSitePartitioningItems && {
                options: 'withSites',
              }),
            },
          ],
        },
        createTitle: (item) => item.name || '--',
        createDescription: (item) => item.description || '',
        createDisabled: createDisabledAvailableItem,
        createTooltip: createAvailableItemTooltip,
      }}
      existingItemsParams={{
        queryOptions: existingItemsQueryOptions,
        createTitle: (item) => item.name || '--',
        createDescription: (item) => item.description || '',
        createDisabled: (item) => {
          if (!isShowingSitePartitioningItems) {
            return false;
          }

          const groupSiteIds =
            item.sites?.map(({ id }: { id: number }) => id) || [];
          const siteIds = sitesData?.map(({ id }: { id: number }) => id) || [];

          /**
           * If the group has site(s) assigned to it and the signed in user
           * doesn't have access to any of those sites, disable the group
           */
          return (
            groupSiteIds.length > 0 &&
            !intersection(groupSiteIds, siteIds).length
          );
        },
        createTooltip: (item) => {
          if (!isShowingSitePartitioningItems) {
            return null;
          }

          const groupSiteIds =
            item.sites?.map(({ id }: { id: number }) => id) || [];
          const siteIds = sitesData?.map(({ id }: { id: number }) => id) || [];

          /**
           * If the group has site(s) assigned to it and the signed in user
           * doesn't have access to any of those sites, disable the group
           */
          const isRoleRestricted =
            groupSiteIds.length > 0 &&
            !intersection(groupSiteIds, siteIds).length;

          return isRoleRestricted
            ? t('Your site-specific role prevents management of this group')
            : null;
        },
      }}
      existingItemIdsParams={{
        queryOptions: existingItemIdsQueryOptions,
      }}
      createLabel={createLabel}
      existingItemsLabel={existingItemsLabel}
      // Custom filter option that checks both title and description
      filterOption={(inputValue, option) => {
        const inputValueLower = inputValue.toLowerCase();
        const titleMatch =
          option.title && option.title.toLowerCase().includes(inputValueLower);
        const descriptionMatch =
          option.description &&
          option.description.toLowerCase().includes(inputValueLower);

        return titleMatch || descriptionMatch || false;
      }}
      {...opSiteSpecificGroupDataFetchTransferProps}
    />
  );
};
